import _ from 'lodash';

import { NotificationTheme } from '../../../constants/notification';
import { NotificationsTranslationKeys } from '../../../containers/notifications/notifications-keys';
import {
  buildLoadDataCachedFn,
  fetchDataOnProjectChange,
} from '../../../redux/actions/router/utils/utils';
import {
  getProjectIdFromRoute,
  getProjectScopeByRoute,
} from '../../../redux/projects/projects.selectors';
import { Dispatch, GetState } from '../../../redux/redux.types';
import { ProjectInOrganizationId } from '../../../types/entities';
import i18n from '../../../utils/i18n';
import { ReplicationFormData } from '../replication-details-panel/replication/add-new-replication/add-new-replication-form.types';
import { replicationUrnPlatform } from '../replications.constants';
import { replicationsService } from '../replications.service';
import {
  Replication,
  ReplicationBodyFilters,
  ReplicationBodyFiltersPayload,
  ReplicationPlatform,
} from '../replications.types';
import { generateReplicationBodyFilters } from '../replications.utils';

import {
  getEditingReplicationActiveStatus,
  getEditingReplicationBodyFilters,
  getEditingReplicationBucketName,
  getEditingReplicationDestination,
  getEditingReplicationId,
  getEditingReplicationName,
  getReplicationById,
} from './replications.selectors';
import { getInitialValues } from '../replication-details-panel/replication/edit-replication/edit-replication-form.utils';
import { getSelectedOrganizationId } from '../../../selectors/organizations/organizations';

export const REPLICATIONS_LIST_ACTION_NAMES = {
  GET_REPLICATIONS_START: 'REPLICATIONS_ACTION_NAMES__GET_REPLICATIONS_START',
  GET_REPLICATIONS_SUCCESS:
    'REPLICATIONS_ACTION_NAMES__GET_REPLICATIONS_SUCCESS',
  GET_REPLICATIONS_ERROR: 'REPLICATIONS_ACTION_NAMES__GET_REPLICATIONS_ERROR',

  CREATE_START: 'REPLICATIONS_ACTION_NAMES__CREATE_START',
  CREATE_SUCCESS: 'REPLICATIONS_ACTION_NAMES__CREATE_SUCCESS',
  CREATE_ERROR: 'REPLICATIONS_ACTION_NAMES__CREATE_ERROR',

  UPDATE_REPLICATION_START:
    'REPLICATIONS_ACTION_NAMES__UPDATE_REPLICATION_START',
  UPDATE_REPLICATION_SUCCESS:
    'REPLICATIONS_ACTION_NAMES__UPDATE_REPLICATION_SUCCESS',
  UPDATE_REPLICATION_ERROR:
    'REPLICATIONS_ACTION_NAMES__UPDATE_REPLICATION_ERROR',

  RENAME_REPLICATION_START:
    'REPLICATIONS_ACTION_NAMES__RENAME_REPLICATION_START',
  RENAME_REPLICATION_SUCCESS:
    'REPLICATIONS_ACTION_NAMES__RENAME_REPLICATION_SUCCESS',
  RENAME_REPLICATION_ERROR:
    'REPLICATIONS_ACTION_NAMES__RENAME_REPLICATION_ERROR',

  REMOVE_REPLICATION_START:
    'REPLICATIONS_ACTION_NAMES__REMOVE_REPLICATION_START',
  REMOVE_REPLICATION_SUCCESS:
    'REPLICATIONS_ACTION_NAMES__REMOVE_REPLICATION_SUCCESS',
  REMOVE_REPLICATION_ERROR:
    'REPLICATIONS_ACTION_NAMES__REMOVE_REPLICATION_ERROR',

  DUPLICATE_REPLICATION_START:
    'REPLICATIONS_ACTION_NAMES__DUPLICATE_REPLICATION_START',
  DUPLICATE_REPLICATION_SUCCESS:
    'REPLICATIONS_ACTION_NAMES__DUPLICATE_REPLICATION_SUCCESS',
  DUPLICATE_REPLICATION_ERROR:
    'REPLICATIONS_ACTION_NAMES__DUPLICATE_REPLICATION_ERROR',

  CHANGE_REPLICATION_ACTIVE_STATUS_START:
    'REPLICATIONS_ACTION_NAMES__CHANGE_REPLICATION_ACTIVE_STATUS_START',
  CHANGE_REPLICATION_ACTIVE_STATUS_SUCCESS:
    'REPLICATIONS_ACTION_NAMES__CHANGE_REPLICATION_ACTIVE_STATUS_SUCCESS',
  CHANGE_REPLICATION_ACTIVE_STATUS_ERROR:
    'REPLICATIONS_ACTION_NAMES__CHANGE_REPLICATION_ACTIVE_STATUS_ERROR',
};

const updateReplicationSuccess = (
  replicationId: string,
  replication: Replication,
) => ({
  type: REPLICATIONS_LIST_ACTION_NAMES.UPDATE_REPLICATION_SUCCESS,
  payload: replication,
  meta: { replicationId },
  notification: {
    key: NotificationsTranslationKeys.replicationsUpdateReplicationSuccess,
    theme: NotificationTheme.Success,
  },
});

const updateReplicationError = (reason?) => ({
  type: REPLICATIONS_LIST_ACTION_NAMES.UPDATE_REPLICATION_ERROR,
  payload: reason,
  notification: {
    key: NotificationsTranslationKeys.replicationsUpdateReplicationError,
    theme: NotificationTheme.Error,
  },
});

export const getReplications = (context, dispatch, getState) => {
  const state = getState();
  const organizationId = getSelectedOrganizationId(state, { router: context });
  const projectId = getProjectIdFromRoute(state);

  if (!organizationId || !projectId) {
    return Promise.reject({
      code: 404,
    });
  }

  return dispatch({
    types: [
      REPLICATIONS_LIST_ACTION_NAMES.GET_REPLICATIONS_START,
      REPLICATIONS_LIST_ACTION_NAMES.GET_REPLICATIONS_SUCCESS,
      REPLICATIONS_LIST_ACTION_NAMES.GET_REPLICATIONS_ERROR,
    ],
    callApi: () =>
      replicationsService.getReplications({ organizationId, projectId }),
  });
};

// export const getReplicationsCached = buildMemoizedLoadFn(getReplications);

export const getReplicationsCached = (context) => (dispatch, getState) => {
  return replicationsLoadDataCached(context, dispatch, getState);
};

const replicationsLoadDataCached = buildLoadDataCachedFn(
  getReplications,
  fetchDataOnProjectChange,
);

function createReplicationCredentials(
  projectContext: ProjectInOrganizationId,
  formData: ReplicationFormData,
) {
  let payload: any = {
    platform: `${replicationUrnPlatform}${formData.destination}`,
    type: 'service-account',
  };

  if (formData.destination === ReplicationPlatform.Aws) {
    payload = {
      ...payload,
      key: {
        accessKey: formData.accessKey,
        accessSecret: formData.accessSecretKey,
      },
    };
  }

  if (formData.destination === ReplicationPlatform.Gcp) {
    payload = {
      ...payload,
      key: {
        encodedContent: btoa(formData.accessJson),
      },
    };
  }

  return replicationsService.createReplicationCredentials(
    projectContext,
    payload,
  );
}

function updateReplicationCredentials(
  projectContext: ProjectInOrganizationId,
  formData: ReplicationFormData,
  replicationId: string,
) {
  return createReplicationCredentials(projectContext, formData).then(
    ({ data }) => {
      const credentialId = _.get(data, 'payload.id');

      if (!credentialId) {
        throw new Error('no credential id');
      }

      return replicationsService.updateReplicationCredentials(
        projectContext,
        replicationId,
        {
          credential: credentialId,
        },
      );
    },
  );
}

function updateReplicationFilters(
  projectContext: ProjectInOrganizationId,
  replicationId: string,
  bodyFilters: ReplicationBodyFiltersPayload,
) {
  return Promise.all([
    replicationsService.updateReplicationCopyWatch(
      projectContext,
      replicationId,
      bodyFilters,
    ),
    replicationsService.updateReplicationDeleteWatch(
      projectContext,
      replicationId,
      bodyFilters,
    ),
    replicationsService.updateReplicationUpdateWatch(
      projectContext,
      replicationId,
      bodyFilters,
    ),
  ]);
}

export function createReplication(formData: ReplicationFormData) {
  return (dispatch: Dispatch, getState: GetState) => {
    const projectContext = getProjectScopeByRoute(getState());

    return createReplicationCredentials(projectContext, formData).then(
      ({ data }) => {
        const credentialId = _.get(data, 'payload.id');

        if (!credentialId) {
          throw new Error('no credential id');
        }

        const bodyFilters = generateReplicationBodyFilters(
          formData.bodyFilters,
        );

        const payload = {
          name: formData.name,
          enabled: formData.active,
          replica: {
            platform: `${replicationUrnPlatform}${formData.destination}`,
            bucket: formData.bucketName,
            credentialId,
          },
          copy: {
            bodyFilters,
          },
          delete: {
            bodyFilters,
          },
          update: {
            bodyFilters,
          },
        };

        return dispatch({
          types: [
            REPLICATIONS_LIST_ACTION_NAMES.CREATE_START,
            {
              name: REPLICATIONS_LIST_ACTION_NAMES.CREATE_SUCCESS,
              notification: {
                message: i18n.t(
                  'replications.notifications.add-new-replication.success',
                ),
                theme: NotificationTheme.Success,
              },
            },
            {
              name: REPLICATIONS_LIST_ACTION_NAMES.CREATE_ERROR,
              notification: {
                message: i18n.t(
                  'replications.notifications.add-new-replication.error',
                ),
                theme: NotificationTheme.Error,
              },
            },
          ],
          callApi: () =>
            replicationsService.createReplication(projectContext, payload),
        });
      },
    );
  };
}

export function removeReplication(replicationId: string) {
  return (dispatch: Dispatch, getState: GetState) => {
    const projectContext = getProjectScopeByRoute(getState());

    return dispatch({
      types: [
        REPLICATIONS_LIST_ACTION_NAMES.REMOVE_REPLICATION_START,
        REPLICATIONS_LIST_ACTION_NAMES.REMOVE_REPLICATION_SUCCESS,
        REPLICATIONS_LIST_ACTION_NAMES.REMOVE_REPLICATION_ERROR,
      ],
      additionalData: {
        replicationId,
      },
      callApi: () =>
        replicationsService.removeReplication(projectContext, replicationId),
    });
  };
}

export function duplicateReplication(replicationId: string) {
  return (dispatch: Dispatch, getState: GetState) => {
    const state = getState();

    const projectContext = getProjectScopeByRoute(state);
    const replication = getReplicationById(state)(replicationId);

    if (!replication) {
      throw new Error(
        `Can't find the replication for replicationId ${replicationId}`,
      );
    }

    const payload = {
      name: `${replication.name} (${i18n.t(
        'replications.duplicate-replication.text.duplicate',
      )})`,
      enabled: replication.enabled,
      replica: replication.replica,
      copy: {
        bodyFilters: generateReplicationBodyFilters(
          replication.copy.on.bodyFilters,
        ),
      },
      delete: {
        bodyFilters: generateReplicationBodyFilters(
          replication.delete.on.bodyFilters,
        ),
      },
      update: {
        bodyFilters: generateReplicationBodyFilters(
          replication.update.on.bodyFilters,
        ),
      },
    };

    return dispatch({
      types: [
        {
          name: REPLICATIONS_LIST_ACTION_NAMES.DUPLICATE_REPLICATION_START,
          notification: {
            message: i18n.t(
              'replications.notifications.duplicate-replication.start',
            ),
            theme: NotificationTheme.Warning,
          },
        },
        {
          name: REPLICATIONS_LIST_ACTION_NAMES.DUPLICATE_REPLICATION_SUCCESS,
          notification: {
            message: i18n.t(
              'replications.notifications.duplicate-replication.success',
            ),
            theme: NotificationTheme.Success,
          },
        },
        {
          name: REPLICATIONS_LIST_ACTION_NAMES.DUPLICATE_REPLICATION_ERROR,
          notification: {
            message: i18n.t(
              'replications.notifications.duplicate-replication.error',
            ),
            theme: NotificationTheme.Error,
          },
        },
      ],
      additionalData: {
        replicationId,
      },
      callApi: () =>
        replicationsService.createReplication(projectContext, payload),
    });
  };
}

export function changeReplicationActiveStatus(newActiveStatus: boolean) {
  return (dispatch: Dispatch, getState: GetState) => {
    const state = getState();

    const replicationId = getEditingReplicationId(state);
    const projectContext = getProjectScopeByRoute(state);

    if (!replicationId) {
      throw new Error('no editing replication id');
    }

    const startTranslationKey = newActiveStatus
      ? NotificationsTranslationKeys.replicationsEnableReplicationStart
      : NotificationsTranslationKeys.replicationsDisableReplicationStart;

    const successTranslationKey = newActiveStatus
      ? NotificationsTranslationKeys.replicationsEnableReplicationSuccess
      : NotificationsTranslationKeys.replicationsDisableReplicationSuccess;

    return dispatch({
      types: [
        {
          name: REPLICATIONS_LIST_ACTION_NAMES.CHANGE_REPLICATION_ACTIVE_STATUS_START,
          notification: {
            key: startTranslationKey,
            theme: NotificationTheme.Warning,
          },
        },
        {
          name: REPLICATIONS_LIST_ACTION_NAMES.CHANGE_REPLICATION_ACTIVE_STATUS_SUCCESS,
          notification: {
            key: successTranslationKey,
            theme: NotificationTheme.Success,
          },
        },
        {
          name: REPLICATIONS_LIST_ACTION_NAMES.CHANGE_REPLICATION_ACTIVE_STATUS_ERROR,
          notification: {
            key: NotificationsTranslationKeys.replicationsChangeReplicationActiveStatusError,
            theme: NotificationTheme.Error,
          },
        },
      ],
      additionalData: {
        replicationId,
        enabled: newActiveStatus,
      },
      callApi: () =>
        replicationsService.changeActiveStatus(
          projectContext,
          replicationId,
          newActiveStatus,
        ),
    });
  };
}

const isReplicationCredentialsChanged = (
  formData: ReplicationFormData,
  initialValues: ReplicationFormData,
): boolean | undefined => {
  if (initialValues.destination === ReplicationPlatform.Aws) {
    return (
      !_.isEmpty(formData.accessKey) && !_.isEmpty(formData.accessSecretKey)
    );
  }

  if (initialValues.destination === ReplicationPlatform.Gcp) {
    return !_.isEmpty(formData.accessJson);
  }
};

const isReplicationNameChanged = (
  formData: ReplicationFormData,
  initialValues: ReplicationFormData,
): boolean | undefined => {
  return initialValues.name !== formData.name;
};

const areReplicationFiltersChanged = (
  formData: ReplicationFormData,
  initialValues: ReplicationFormData,
): boolean => {
  return !_.isEqual(formData.bodyFilters, initialValues.bodyFilters);
};

const areReplicationFiltersRemoved = (
  formData: ReplicationFormData,
  initialValues: ReplicationFormData,
): boolean =>
  formData.bodyFilters.length !== initialValues.bodyFilters.length ||
  formData.bodyFilters.some((formDataFilter, index) =>
    Object.keys(formDataFilter).some(
      (key) =>
        formDataFilter[key as keyof ReplicationBodyFilters] === undefined,
    ),
  );

export function updateReplication(formData: ReplicationFormData) {
  return (dispatch: Dispatch, getState: GetState) => {
    const state = getState();

    const projectContext = getProjectScopeByRoute(state);
    const replicationId = getEditingReplicationId(state);

    if (!replicationId) {
      throw new Error('no editing replication id');
    }

    // const initialValues = getFormInitialValues(REPLICATION_EDIT_FORM_NAME)(
    //   state,
    // ) as ReplicationFormData;
    const initialValues: ReplicationFormData = {
      ...getInitialValues(),
      active: getEditingReplicationActiveStatus(state),
      bucketName: getEditingReplicationBucketName(state),
      destination: getEditingReplicationDestination(state),
      bodyFilters: getEditingReplicationBodyFilters(state),
      name: getEditingReplicationName(state),
    };
    let credentialsUpdateRequest;
    let nameUpdateRequest;
    let filtersUpdateRequest;

    if (isReplicationCredentialsChanged(formData, initialValues)) {
      credentialsUpdateRequest = updateReplicationCredentials(
        projectContext,
        formData,
        replicationId,
      );
    }

    if (isReplicationNameChanged(formData, initialValues)) {
      nameUpdateRequest = replicationsService.renameReplication(
        projectContext,
        replicationId,
        formData.name,
      );
    }

    if (
      areReplicationFiltersChanged(formData, initialValues) ||
      areReplicationFiltersRemoved(formData, initialValues)
    ) {
      const bodyFilters = generateReplicationBodyFilters(formData.bodyFilters);

      filtersUpdateRequest = updateReplicationFilters(
        projectContext,
        replicationId,
        bodyFilters,
      );
    }

    return Promise.all([
      credentialsUpdateRequest,
      nameUpdateRequest,
      filtersUpdateRequest,
    ])
      .then(() =>
        replicationsService
          .getReplication(
            {
              organizationId: projectContext.organizationId,
              projectId: projectContext.project.id,
            },
            replicationId,
          )
          .then((data) =>
            dispatch(updateReplicationSuccess(replicationId, data)),
          ),
      )
      .catch((error) => {
        dispatch(updateReplicationError(error));

        return Promise.reject(error);
      });
  };
}

export function renameReplication(replicationId: string, newName: string) {
  return (dispatch: Dispatch, getState: GetState) => {
    const projectContext = getProjectScopeByRoute(getState());

    return dispatch({
      types: [
        REPLICATIONS_LIST_ACTION_NAMES.RENAME_REPLICATION_START,
        REPLICATIONS_LIST_ACTION_NAMES.RENAME_REPLICATION_SUCCESS,
        REPLICATIONS_LIST_ACTION_NAMES.RENAME_REPLICATION_ERROR,
      ],
      additionalData: {
        replicationId,
        newName,
      },
      callApi: () =>
        replicationsService.renameReplication(
          projectContext,
          replicationId,
          newName,
        ),
    });
  };
}
